import { buildLocalStorageKey } from '@risksmart-app/components/src/hooks/useLocalStorage';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useCallback, useMemo } from 'react';

import { handleError } from '@/utils/errorUtils';

/** Resized column widths in pixels, keyed by the table's column id (`<dataSourceIndex>|<fieldId>`). */
export type CustomDatasourceColumnWidths = Record<string, number>;

/**
 * Every surface's widths in one blob, keyed by `scope`. A single registered
 * `LocalStorageKeys` entry can't express a key per datasource/widget, and one blob
 * keyed internally gives the same isolation as a key each.
 */
type ColumnWidthStore = Record<string, CustomDatasourceColumnWidths>;

const STORAGE_KEY = 'CustomDatasourceColumnWidths-PreferencesV1';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Builds the `scope` identifying one CDS table instance. Widths are deliberately NOT
 * shared between the native view, the editor preview and each widget instance — each
 * may show a different field selection and filter scope, so a width that suits one
 * is wrong for another (CUS-370 AC3).
 */
export const buildColumnWidthScope = {
  /** The native CDS view for one saved datasource. */
  view: (customDatasourceId: string) => `view:${customDatasourceId}`,
  /** The preview table on the CDS create/edit page. */
  editor: (customDatasourceId: string) => `editor:${customDatasourceId}`,
  /** One CDS table widget instance on a dashboard. */
  widget: (widgetId: string) => `widget:${widgetId}`,
};

/**
 * Per-user, per-instance persistence for CDS table column widths.
 *
 * localStorage — not the datasource record and not the dashboard's widget settings —
 * because a width is a personal reading preference (AC4), and widget settings are
 * stored on the dashboard, so on a shared dashboard they would apply to every viewer.
 * This also matches how every register table persists its widths
 * (`useGetTableProps` → `useOrgScopedLocalStorage`), which AC15 asks for.
 *
 * A `scope` of `undefined` (an unsaved datasource on the create page, or a widget
 * being previewed before it is added to a dashboard) makes the hook read-only: the
 * columns still resize, the widths just have nowhere stable to belong.
 *
 * Both directions talk to localStorage directly, through one key derived from the
 * CURRENT `orgKey`. Deliberately not `useOrgScopedLocalStorage`, whose setter would
 * be wrong here on two counts, the first a correctness bug rather than a preference:
 *
 * 1. Several CDS tables can be mounted at once — a dashboard with two table widgets
 *    is the ordinary case. That hook holds a `useState` copy of the WHOLE blob per
 *    instance, taken at its own mount, and its setter applies a functional update to
 *    that copy rather than to current storage. Resizing widget A then widget B would
 *    write B's stale copy and erase A's widths. Merging against the live record at
 *    write time is what keeps the scopes independent.
 * 2. The setter also sets React state, re-rendering the table on every drag. Nothing
 *    needs that: Cloudscape only seeds column widths at mount and ignores later
 *    `width` prop changes, so the re-render would repaint the table to no effect.
 *
 * The read is re-derived whenever the key changes rather than snapshotted at mount,
 * which keeps it symmetric with the write. That symmetry is the point: a hook that
 * reads under a mount-time key and writes under a current one can serve one
 * organisation's widths while persisting to another's. Not reachable today — the only
 * org switch is `loginWithRedirect()`, a full page redirect that unmounts everything —
 * but the asymmetry is a latent trap for no benefit, and removing it also drops a
 * dependency. `useDashboardPreferencesSync` bypasses the same setter for reason 2.
 */
export const useCustomDatasourceColumnWidths = (
  scope: string | undefined
): [
  CustomDatasourceColumnWidths | undefined,
  (widths: CustomDatasourceColumnWidths) => void,
] => {
  const { user } = useRisksmartUser();

  // `buildLocalStorageKey` is exported for callers that touch localStorage directly.
  // `orgKey` absent → the bare key, matching `useOrgScopedLocalStorage`'s own
  // fallback, so a value written before the user resolved is still found afterwards.
  const storageKey = buildLocalStorageKey(STORAGE_KEY, user?.orgKey ?? null);

  const readStore = useCallback((): ColumnWidthStore => {
    try {
      const persisted = window.localStorage.getItem(storageKey);
      if (!persisted) {
        return {};
      }

      // Parsing without throwing is NOT the same as valid. `JSON.parse` succeeds
      // on `null`, `[]`, `3` and `"x"`, none of which is a store — and `null` is
      // the dangerous one, because the scope lookup below throws on it rather
      // than yielding undefined, which would take the whole table down on a
      // render. The try/catch cannot help: nothing was thrown at parse time.
      // Anything not shaped like a store is treated as absent, per the same
      // reasoning as the catch — a lost width is not worth breaking the table.
      const parsed: unknown = JSON.parse(persisted);
      if (!isPlainObject(parsed)) {
        return {};
      }

      const store: ColumnWidthStore = {};
      for (const [scopeKey, scopeWidths] of Object.entries(parsed)) {
        if (!isPlainObject(scopeWidths)) {
          continue;
        }

        const widths: CustomDatasourceColumnWidths = {};
        for (const [columnId, width] of Object.entries(scopeWidths)) {
          // The declared type says number; parsed JSON cannot be trusted to
          // honour it, and a non-numeric width reaches Cloudscape's `width` prop.
          if (typeof width === 'number' && Number.isFinite(width)) {
            widths[columnId] = width;
          }
        }
        store[scopeKey] = widths;
      }

      return store;
    } catch (error) {
      // Private mode, quota, or genuinely unparseable content.
      handleError(error);

      return {};
    }
  }, [storageKey]);

  const setWidths = useCallback(
    (widths: CustomDatasourceColumnWidths) => {
      if (!scope) {
        return;
      }
      try {
        window.localStorage.setItem(
          storageKey,
          JSON.stringify({ ...readStore(), [scope]: widths })
        );
      } catch (error) {
        handleError(error);
      }
    },
    [scope, storageKey, readStore]
  );

  const widths = useMemo(
    () => (scope ? readStore()[scope] : undefined),
    [scope, readStore]
  );

  return [widths, setWidths];
};
